/**
 * SettleCompleteDialog — pops up when a broker drags a deal to Settled
 * but the deal is missing required info (Lender, NAF, Loan Term,
 * Interest Rate). Instead of the drag just snapping back with a red
 * banner, the modal shows only the missing fields as a form. Broker
 * fills them, clicks Save & Settle → we PATCH the deal, then re-fire
 * the move to `settled`.
 *
 * On cancel or close, the card snaps back to its previous stage (that
 * revert is handled by the mutation onError in PipelinePage).
 *
 * Per Andy 2026-08-31: keep the modal tight — only show what's missing,
 * plus settlement date so backdating is easy. Skip balloon/commission
 * to avoid feeling like "just open the deal page".
 */
import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Loader2, AlertCircle } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { dealsApi, lendersApi } from "@/lib/api"

/** Shape returned by dealsApi.move — mirrored here to avoid a wide import. */
type MoveResult = {
  ok: true
  transitionedIntoSettled?: boolean
  settledEnrolAutomationIds?: string[]
}

export type SettleCompleteDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  dealId: string
  /** Field-error keys returned by the API — drives which inputs render. */
  missingFields: string[]
  /**
   * Called on successful save + settle. Parent should invalidate caches
   * and, if `result.transitionedIntoSettled` is true with enrol ids, open
   * the on-settled enrol dialog so the flow matches a normal drop.
   */
  onSettled: (result: MoveResult) => void
  /** Called when the broker cancels; parent should revert the optimistic drop. */
  onCancel: () => void
}

/** Human labels for the field keys the API returns in fieldErrors. */
const FIELD_LABEL: Record<string, string> = {
  lenderId: "Lender",
  naf: "NAF",
  termMonths: "Loan Term (months)",
  writingRate: "Interest Rate (%)",
  settledDate: "Settlement Date",
  balloon: "Balloon Amount",
}

/** Today's date as YYYY-MM-DD (local). */
function today(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

export function SettleCompleteDialog({
  open,
  onOpenChange,
  dealId,
  missingFields,
  onSettled,
  onCancel,
}: SettleCompleteDialogProps) {
  const qc = useQueryClient()

  // Always include settlement date so brokers can backdate if needed;
  // if the API didn't complain about it, we default to today but still
  // let them override.
  const fieldsToShow = useMemo(() => {
    const set = new Set(missingFields)
    set.add("settledDate")
    return set
  }, [missingFields])

  // Form state — reset every time the dialog opens.
  const [lenderId, setLenderId] = useState<string>("")
  const [naf, setNaf] = useState<string>("")
  const [termMonths, setTermMonths] = useState<string>("")
  const [writingRate, setWritingRate] = useState<string>("")
  const [settledDate, setSettledDate] = useState<string>(today())
  // Balloon is optional — always shown so brokers can add it here
  // instead of opening the deal detail page. Empty = no balloon.
  const [balloon, setBalloon] = useState<string>("")
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setLenderId("")
      setNaf("")
      setTermMonths("")
      setWritingRate("")
      setSettledDate(today())
      setBalloon("")
      setFormError(null)
    }
  }, [open])

  // Lenders list for the dropdown — only load when the dialog needs it.
  const lenders = useQuery({
    queryKey: ["lenders-active"],
    queryFn: () => lendersApi.list({ active: "true", sort: "name" }),
    enabled: open && fieldsToShow.has("lenderId"),
  })

  const saveAndSettle = useMutation({
    mutationFn: async () => {
      // Validate — only the fields we're actually asking about.
      const patch: Record<string, unknown> = {}
      if (fieldsToShow.has("lenderId")) {
        if (!lenderId) throw new Error("Please select a lender")
        patch.lenderId = lenderId
      }
      if (fieldsToShow.has("naf")) {
        const n = parseFloat(naf)
        if (!Number.isFinite(n) || n <= 0)
          throw new Error("NAF must be a positive number")
        patch.naf = n
      }
      if (fieldsToShow.has("termMonths")) {
        const t = parseInt(termMonths, 10)
        if (!Number.isFinite(t) || t <= 0)
          throw new Error("Loan term must be a positive whole number of months")
        patch.termMonths = t
      }
      if (fieldsToShow.has("writingRate")) {
        const r = parseFloat(writingRate)
        if (!Number.isFinite(r) || r <= 0)
          throw new Error("Interest rate must be a positive number")
        patch.writingRate = r
      }
      // Settlement date always sent — either the default (today) or the
      // broker's override. Empty is caught here rather than at the API.
      if (!settledDate) throw new Error("Settlement date is required")
      patch.settledDate = settledDate

      // Balloon is optional. Only send it if the broker filled something in.
      // Non-numeric / non-positive input is treated as an error so we don't
      // silently drop a typo.
      const balloonTrimmed = balloon.trim()
      if (balloonTrimmed) {
        const b = parseFloat(balloonTrimmed)
        if (!Number.isFinite(b) || b < 0)
          throw new Error("Balloon amount must be zero or a positive number")
        patch.balloon = b
      }

      // 1. PATCH the deal with the new field values.
      await dealsApi.update(dealId, patch)

      // 2. Now re-fire the move to settled. The API will re-validate; if
      //    something is still missing it'll bounce back with fieldErrors
      //    and the caller can show them.
      const res = await dealsApi.move(dealId, "settled")
      return res
    },
    onSuccess: (result) => {
      // Invalidate caches so the pipeline board and any open deal detail
      // see the fresh values.
      qc.invalidateQueries({ queryKey: ["deals-pipeline"] })
      qc.invalidateQueries({ queryKey: ["deals"] })
      qc.invalidateQueries({ queryKey: ["deal", dealId] })
      onSettled(result)
      onOpenChange(false)
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : "Save failed")
    },
  })

  const busy = saveAndSettle.isPending

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return
        if (!next) onCancel()
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Complete deal to settle</DialogTitle>
          <DialogDescription>
            Add the missing information below to move this deal into
            Settled. All fields are required for commission tracking,
            balloon reminders, and lender reports.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {fieldsToShow.has("lenderId") && (
            <div className="grid gap-1.5">
              <Label htmlFor="lender">
                {FIELD_LABEL.lenderId} <span className="text-red-500">*</span>
              </Label>
              <select
                id="lender"
                value={lenderId}
                onChange={(e) => setLenderId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                disabled={busy || lenders.isLoading}
              >
                <option value="">
                  {lenders.isLoading ? "Loading lenders…" : "Select a lender…"}
                </option>
                {lenders.data?.items.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {fieldsToShow.has("naf") && (
            <div className="grid gap-1.5">
              <Label htmlFor="naf">
                {FIELD_LABEL.naf} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="naf"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="e.g. 45000"
                value={naf}
                onChange={(e) => setNaf(e.target.value)}
                disabled={busy}
                autoFocus={!fieldsToShow.has("lenderId")}
              />
            </div>
          )}

          {fieldsToShow.has("termMonths") && (
            <div className="grid gap-1.5">
              <Label htmlFor="termMonths">
                {FIELD_LABEL.termMonths} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="termMonths"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                placeholder="e.g. 60"
                value={termMonths}
                onChange={(e) => setTermMonths(e.target.value)}
                disabled={busy}
              />
            </div>
          )}

          {fieldsToShow.has("writingRate") && (
            <div className="grid gap-1.5">
              <Label htmlFor="writingRate">
                {FIELD_LABEL.writingRate}{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="writingRate"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="e.g. 9.95"
                value={writingRate}
                onChange={(e) => setWritingRate(e.target.value)}
                disabled={busy}
              />
            </div>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="settledDate">{FIELD_LABEL.settledDate}</Label>
            <Input
              id="settledDate"
              type="date"
              value={settledDate}
              onChange={(e) => setSettledDate(e.target.value)}
              disabled={busy}
            />
            <p className="text-xs text-muted-foreground">
              Defaults to today. Backdate if the deal already settled.
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="balloon">
              {FIELD_LABEL.balloon}{" "}
              <span className="text-xs font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <Input
              id="balloon"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder="e.g. 15000"
              value={balloon}
              onChange={(e) => setBalloon(e.target.value)}
              disabled={busy}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank if there's no balloon on this deal.
            </p>
          </div>

          {formError && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{formError}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onCancel()
              onOpenChange(false)
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={() => saveAndSettle.mutate()}
            disabled={busy}
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save & Settle"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
